import { createIframeApp } from './createIframeApp.js'

export async function embedComponent (iframeNode, data, opts = {}) {
  if (!iframeNode || !data) return
  const {
    key,
    width,
    height,
    fullsize = false,
    // Canvas "convert to frame" mode: share the parent's chrome context
    // (designSystem, methods, functions) with the embedded frame so its
    // tokens resolve the same way canvas chrome does, and canvas's own
    // chrome-level helpers remain reachable via el.call / methods.
    // Pass `shareContext: { designSystem, methods?, functions? }` to opt in.
    shareContext
  } = opts
  if (!key) return

  // Resolve the component/page schema off the raw project data before we
  // hand it to createIframeApp (the helper deep-clones into the iframe
  // realm, so resolving after would operate on a clone — equivalent but
  // needlessly indirect).
  const factory = key.startsWith('/') ? 'pages' : 'components'
  const schema = data[factory]?.[key] || data.components?.[key]

  const hasExplicitBox = width != null || height != null
  const inner = schema
    ? (fullsize
      ? { ...(schema.props?.demoComponent || schema), boxSize: '100dvh 100dvw' }
      : (schema.props?.demoComponent || schema))
    : {
      tag: 'div',
      text: `No ${factory.slice(0, -1)} registered for key "${key}"`,
      style: { padding: '24px', font: '14px system-ui', color: '#999' }
    }

  // DOMQL treats `/…` child keys as router paths rather than components,
  // so a page whose key starts with `/` (e.g. `/transactions`) wouldn't
  // mount under its own key. Wrap it under a valid identifier and let
  // the page schema itself render underneath.
  const shellKey = schema ? (key.startsWith('/') ? 'Page' : key) : 'Fallback'
  const shell = {
    flow: 'x',
    align: 'center center',
    boxSize: hasExplicitBox ? `${height || '100dvh'} ${width || '100dvw'}` : '100dvh 100dvw',
    overflow: 'auto',
    theme: 'document',
    // Hoist `data.app.fetch` onto the root shell. The fetch plugin reads
    // `element.fetch` in its `create` hook; without this, embedded pages
    // mount but their project-level queries never fire (canvas tiles show
    // "Loading…" while preview renders real data through the same data).
    ...(data.app?.fetch ? { fetch: data.app.fetch } : {}),
    [shellKey]: inner
  }

  const contextOverrides = { pages: undefined, app: undefined, router: false }
  if (shareContext) {
    // Explicit non-isolation — frame inherits the caller's designSystem /
    // methods / functions. This breaks the preview-iframe isolation
    // contract on purpose: canvas needs tile frames to keep canvas chrome
    // tooling reachable, which requires the shared objects.
    if (shareContext.designSystem) contextOverrides.designSystem = shareContext.designSystem
    if (shareContext.methods) contextOverrides.methods = { ...(data.methods || {}), ...shareContext.methods }
    if (shareContext.functions) contextOverrides.functions = { ...(data.functions || {}), ...shareContext.functions }
  }

  return createIframeApp(iframeNode, data, shell, {
    path: '/component/' + key,
    contextOverrides
  })
}
